import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { CircularProgress } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/ErrorOutline';

import KitsIcon from '../../../assets/icons/KitIcon';

import Modal from '../../shared/modal';
import Checkbox from '../../shared/Checkbox';
import Button from '../../shared/Button';
import Input from '../../shared/Input';

import { getSections } from '../../../actions/kitsActions';
import { checkAllocationAvailability, getSetsByIds } from '../../../actions/setsActions';

import { useAlert } from '../../../hooks';

import { setsUtils } from '../../../utils';

import {
  setAllocationStatuses,
  setPositionOptions,
  kitTypeOptions,
  kitVariantTypes,
  consumableStatuses,
} from '../../../constants/enums';

const CaseSetsModal = (props) => {
  const {
    open,
    onUnassignClick,
    onClose,
    selected = [],
    onSubmit,
    loading,
    unassignLoading,
    kitId,
    kits,
    status,
    initialConsumables,
    datePassed,
    quantity,
    labels = [],
    kitVariant,
    caseHospital,
    items = [],
    isConsignment,
  } = props;

  const dispatch = useDispatch();

  const [setIds, onChangeIds] = useState(selected || []);
  const [enabled, setEnabled] = useState(true);
  const [search, setSearch] = useState('');
  const [consumables, setConsumables] = useState(initialConsumables || []);
  const [sections, setSections] = useState([]);
  const [sets, setSets] = useState([]);

  const [setsLoading, setSetsLoading] = useState(false);
  const [sectionsLoading, setSectionsLoading] = useState(false);

  const { showAlert } = useAlert();

  useEffect(() => {
    onChangeIds(open ? (selected || []) : []);
  }, [open]);

  useEffect(() => {
    if (open && props.sets?.length) {
      fetchSections().catch();
      fetchSets().catch();
    }
  }, [open, props.sets]);

  useEffect(() => {
    if (initialConsumables && initialConsumables.length) {
      setConsumables(initialConsumables);
    } else {
      const arr = Array(quantity).fill({
        label: '',
        status: consumableStatuses.RETURNED.value,
      });
      setConsumables(arr?.map((item) => ({ ...item, id: uuid() })));
    }
  }, [open, initialConsumables, quantity]);

  useEffect(() => {
    const initial = initialConsumables && initialConsumables.length ? initialConsumables?.filter((item) => !!item) : [];
    setEnabled(status !== setAllocationStatuses.DISABLED.value && (!initial || !initial.length));
  }, [status, initialConsumables]);

  useEffect(() => {
    if (kitId && kits) {
      const selectedKit = kits.find((item) => item.id === kitId);
      if (selectedKit && selectedKit.type === kitTypeOptions.CONSUMABLES.value && !selected.length) {
        setEnabled(false);
      }
    }
  }, [kitId, kits, open, selected]);

  const fetchSections = async () => {
    try {
      if (!sections?.length) {
        setSectionsLoading(true);
      }
      const res = await dispatch(getSections(kitId));
      if (res && res.length) {
        setSections(res);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSectionsLoading(false);
    }
  };

  const fetchSets = async () => {
    try {
      const setIds = props.sets?.map((s) => s.id);
      setSetsLoading(true);
      const res = await dispatch(getSetsByIds(setIds));
      if (res) {
        setSets(res.map((set) => ({ ...set, status: getStatus(set) })));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSetsLoading(false);
    }
  };

  const getStatus = (set) => {
    if (!set.quarantined && !set.consigned && !set.caseAllocation) {
      // Batch Control
    } else {
      return set.quarantined ? (
        setPositionOptions.QUARANTINED.value
      ) : (
        set.consigned ? (
          setPositionOptions.CONSIGNED.value
        ) : (
          // set.caseAllocation && set.caseAllocation !== activeCase.id ? setPositionOptions.ALLOCATED.value : setPositionOptions.AVAILABLE.value
          set.caseAllocation ? setPositionOptions.ALLOCATED.value : setPositionOptions.AVAILABLE.value
        )
      );
    }
  };

  const handleClick = (userId) => {
    const temp = setIds.slice();
    if (temp.includes(userId)) {
      const index = temp.indexOf(userId);

      temp.splice(index, 1);
      onChangeIds(temp);
      return;
    }

    temp.push(userId);
    onChangeIds(temp);
  };

  const handleSubmit = async () => {
    const status = enabled ? setAllocationStatuses.ASSIGNED.value : setAllocationStatuses.DISABLED.value;

    const available = await dispatch(checkAllocationAvailability(setIds));
    if (available) {
      await onSubmit(status, setIds, consumables.filter((c) => !!c?.label));
    } else {
      showAlert('error', 'The set you selected has changed status. Please check the updated details and reselect as required!');
      await fetchSets(setIds);
      onChangeIds([]);
    }
  };

  const handleClose = () => {
    onChangeIds([]);
    setEnabled(true);
    setSearch('');
    onClose();
  };

  const onChangeConsumable = (id, value) => {
    const temp = [...consumables];
    const updatedConsumable = temp?.find((c) => c.id === id);
    if (updatedConsumable) {
      updatedConsumable.label = value;
      setConsumables(temp);
    }
  };

  const getLabelNames = (labelIds = []) => (
    labelIds.map((id) => {
      const label = labels.find((item) => item.id === id);
      return label ? label.name : '';
    })?.join(', ')
  );

  const kit = useMemo(() => {
    const allocationKit = kits.find((item) => item.id === kitId);
    return allocationKit || {};
  }, [kitId, kits]);

  const list = useMemo(() => {
    const setsList = sets?.length ? sets : props.sets;
    const _sets = kitVariant === kitVariantTypes.consignment ? (
      setsList?.filter((set) => !!set.consigned && set?.consignment?.hospital === caseHospital)
    ) : setsList;
    return _sets?.filter((set) => set?.number?.toLowerCase().includes(search?.toLowerCase()));
  }, [search, sets, kitVariant, props.sets]);

  const getLabel = useCallback((set) => {
    if (set?.targetTurns && kit?.target?.value && set?.targetTurns >= kit?.target?.value) {
      return <span className='font-bold' style={{ color: setPositionOptions.QUARANTINED.color }}>Turn Limit</span>;
    }

    let status = set.status;

    if (!set.quarantined && !set.consigned && !set.caseAllocation) {
      if (sections?.some((section) => section?.items?.some((item) => {
        const _item = items?.find((i) => i.id === item.id);
        if (_item && _item?.batchControl && (!set?.batchItems || set?.batchItems[item.id] === undefined || set?.batchItems[item.id]?.length < item?.quantity)) {
          return true;
        }
        if (_item && !_item?.batchControl && (set?.itemsQuantity && set?.itemsQuantity[item.id] !== undefined && set?.itemsQuantity[item.id] < item?.quantity)) {
          return true;
        }
        return false;
      }))) {
        status = setPositionOptions.INCOMPLETE.value;
      } else {
        status = setPositionOptions.AVAILABLE.value;
      }
    }

    return setsUtils.getPositionLabel(status);
  }, [kit, sections, items]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title='Set Allocation'
      fullWidth
    >
      <div className='case-sets-modal__container'>
        <div className='d-flex m-b-lg'>
          <KitsIcon fontSize='small' color='var(--app-text)' />
          <span className='font-bold m-l-md'>{kit.name}</span>
          <span className='secondary m-l-md font-bold'>{kit.kitId}</span>
        </div>

        {datePassed && !isConsignment && (
          <div className='d-flex align-start m-b-lg'>
            <WarningIcon style={{ color: setAllocationStatuses.TRANSFERRED.color, marginRight: 4 }} />
            <div>
              <div className='font-bold' style={{ color: setAllocationStatuses.TRANSFERRED.color }}>
                Update case to future date to assign sets!
              </div>
            </div>
          </div>
        )}

        <Input
          type='switch'
          name='enabled'
          value={enabled}
          onChange={(value) => {
            setEnabled(value);
            onChangeIds([]);
          }}
          placeholder='Assign sets'
        />

        {!enabled && (
          <div className='m-b-sm'>
            <div className='field__title m-b-md'>Consumables</div>
            {consumables?.map((consumable, index) => (
              <Input
                key={consumable?.id || index}
                value={consumable.label}
                type='text'
                onChange={(e) => onChangeConsumable(consumable?.id || index, e.target.value)}
                placeholder={`${index + 1}. Add LOT or Serial No.`}
              />
            ))}
          </div>
        )}

        {enabled && (
          <>
            <Input
              type='search'
              placeholder='Search'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className='m-b-md secondary'>Sets</div>
            <div className='m-b-lg form__select-users' style={{ position: 'relative' }}>
              {(sectionsLoading || setsLoading) && (
                <div
                  className='d-flex p-lg flex-center align-center'
                  style={{
                    position: 'absolute',
                    height: 'calc(100% - 32px)',
                    width: 'calc(100% - 32px)',
                    background: 'rgba(227, 230, 233, 0.52)',
                    zIndex: 1000,
                    borderRadius: '8px'
                  }}
                >
                  <CircularProgress size={40} />
                </div>
              )}

              {list.map((set) => (
                <div className='md-user-info' key={set.id}>
                  <Checkbox
                    input={{
                      onClick: () => handleClick(set.id),
                      checked: setIds.indexOf(set.id) !== -1
                    }}
                    fontSize='small'
                    label={`${set.number}${set.labels && !!set.labels.length ? ` (${getLabelNames(set.labels)})`: ''}`}
                    disabled={
                      (kitVariant !== kitVariantTypes.consignment && set.caseAllocation) || !enabled
                      || (!selected?.includes(set.id) && datePassed && !isConsignment) || set.quarantined
                      || (set.consigned && kitVariant !== kitVariantTypes.consignment)
                      || (set?.targetTurns && kit?.target?.value && set?.targetTurns >= kit?.target?.value)
                      || selected?.length
                    }
                  />
                  <div className='font-size-sm'>
                    {getLabel(set)}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className='form__actions p-t-lg'>
          <Button
            type='submit'
            text='Confirm'
            onClick={handleSubmit}
            disabled={loading || (!enabled && consumables?.some((c) => !c?.label)) || setIds?.length > quantity}
            loading={loading}
          />

          <Button
            type='outlined'
            text='Unassign'
            onClick={onUnassignClick}
            loading={unassignLoading}
            disabled={unassignLoading || !setIds?.length}
          />
        </div>
      </div>
    </Modal>
  );
};

export default CaseSetsModal;
